import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { usePageContext } from '../context/PageContext';
import { supabase } from '../lib/supabase';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Users, Clock, BookOpen, AlertTriangle, Star, Quote, Sparkles, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import SignalsDialog from '../components/students/SignalsDialog';
import { defaultPolicy } from '../lib/issuePolicy';
import { ISSUE_SELECT_COLUMNS, countOpenIssues } from '../lib/issueCounting';

const ENG_NA_TIP = "Not enough data yet. Engagement is rated once more of this student's lessons have been analysed.";

const SESSIONS_PER_PAGE = 15;

// PostgREST caps a response at 1000 rows and long URLs fail, so risks are read
// in session-id chunks with each chunk paged until it stops hitting the cap.
const ID_CHUNK = 200;
const PAGE_SIZE = 1000;

const fetchRisksBySessionIds = async (sessionIds) => {
  const ids = Array.isArray(sessionIds) ? sessionIds : [];
  if (ids.length === 0) return [];
  const chunks = [];
  for (let i = 0; i < ids.length; i += ID_CHUNK) chunks.push(ids.slice(i, i + ID_CHUNK));

  const perChunk = await Promise.all(chunks.map(async (chunk) => {
    const rows = [];
    for (let from = 0; ; from += PAGE_SIZE) {
      const { data, error } = await supabase
        .from('risks_v2')
        .select(ISSUE_SELECT_COLUMNS)
        .in('session_id', chunk)
        .range(from, from + PAGE_SIZE - 1);
      if (error || !data) break;
      rows.push(...data);
      if (data.length < PAGE_SIZE) break;
    }
    return rows;
  }));

  return perChunk.flat();
};

const StudentDetail = () => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { studentName: rawName } = useParams();
  const studentName = decodeURIComponent(rawName);
  const { setSubtitle } = usePageContext();
  const isDark = theme === 'dark';
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionPage, setSessionPage] = useState(0);
  const [openDialog, setOpenDialog] = useState(null); // 'review' | 'enthusiasm' | null

  useEffect(() => {
    if (!organizationId || !studentName) return;
    setSubtitle(studentName);
    loadDetail();
    return () => setSubtitle('');
  }, [organizationId, studentName]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      // Fetch sessions and the org's guardrail policy in parallel; risks come
      // after, keyed off the session ids.
      const [{ data: sessions }, { data: orgRow }] = await Promise.all([
        supabase
          .from('sessions')
          .select('id, tutor_name, lesson_timestamp, duration_minutes, subject_covered, student_engagement_level, analysis_json, session_id, lessonspace_session_id, lessonspace_recording_link')
          .eq('organization_id', organizationId)
          .eq('student_name', studentName)
          .eq('is_processed_v2', true)
          .order('lesson_timestamp', { ascending: false }),
        supabase
          .from('organizations')
          .select('issue_filter_policy')
          .eq('id', organizationId)
          .single(),
      ]);

      const issuePolicy = orgRow?.issue_filter_policy || defaultPolicy();
      const sessData = sessions || [];
      const sessionIds = sessData.map(s => s.id);

      const allRisks = await fetchRisksBySessionIds(sessionIds);

      const risksBySession = {};
      allRisks.forEach(r => {
        if (!r) return;
        if (!risksBySession[r.session_id]) risksBySession[r.session_id] = [];
        risksBySession[r.session_id].push(r);
      });

      let totalMinutes = 0;
      let engHigh = 0, engMed = 0, engLow = 0;
      const tutorMap = {};
      let totalIssues = 0;
      const reviewMoments = [];
      const enthusiasmSignals = [];

      for (const s of sessData) {
        totalMinutes += s.duration_minutes || 0;
        const eng = (s.student_engagement_level || '').toLowerCase();
        if (eng === 'high') engHigh++;
        else if (eng === 'medium') engMed++;
        else if (eng === 'low') engLow++;

        const tName = s.tutor_name || 'Unknown';
        if (!tutorMap[tName]) tutorMap[tName] = { name: tName, sessions: 0, minutes: 0, engHigh: 0, engMed: 0, engLow: 0, issues: 0 };
        tutorMap[tName].sessions++;
        tutorMap[tName].minutes += s.duration_minutes || 0;
        if (eng === 'high') tutorMap[tName].engHigh++;
        else if (eng === 'medium') tutorMap[tName].engMed++;
        else if (eng === 'low') tutorMap[tName].engLow++;

        // Single source of truth for "flagged issue": lib/issueCounting.js.
        const openRisks = countOpenIssues(risksBySession[s.id], issuePolicy);
        tutorMap[tName].issues += openRisks;
        totalIssues += openRisks;

        // Parse analysis_json for review moments and enthusiasm
        const lessonspaceSessionId = s.lessonspace_session_id || s.session_id || null;
        const lessonspaceRecordingLink = s.lessonspace_recording_link || null;
        if (s.analysis_json) {
          const aj = typeof s.analysis_json === 'string' ? JSON.parse(s.analysis_json) : s.analysis_json;
          if (aj.review_worthy_moments) {
            for (const m of aj.review_worthy_moments) {
              reviewMoments.push({
                sessionId: s.id,
                date: s.lesson_timestamp,
                tutor: s.tutor_name,
                quote: m.student_quote || m.quote || m.text || '',
                reason: m.why_review_worthy || m.reason || '',
                contextBefore: m.context_before || '',
                contextAfter: m.context_after || '',
                timestampDisplay: m.timestamp_display || null,
                timestampSeconds: typeof m.timestamp_seconds === 'number' ? m.timestamp_seconds : null,
                lessonspaceSessionId,
                lessonspaceRecordingLink,
              });
            }
          }
          if (aj.positive_indicators) {
            for (const p of aj.positive_indicators) {
              const indicatorName = p.indicator || p.type;
              // WHY: schema requires all 10 indicators present with detected:false placeholders;
              // only surface signals where the AI actually flagged enthusiasm.
              const isDetected = p.detected !== false;
              if ((indicatorName === 'student_enthusiasm') && isDetected) {
                enthusiasmSignals.push({
                  sessionId: s.id,
                  date: s.lesson_timestamp,
                  tutor: s.tutor_name,
                  quote: p.verbatim_quote || p.quote || p.evidence || '',
                  description: p.factual_description || p.description || p.explanation || p.risk_explanation || '',
                  timestampDisplay: p.timestamp_display || null,
                  timestampSeconds: typeof p.timestamp_seconds === 'number' ? p.timestamp_seconds : null,
                  individualGrade: p.individual_grade || null,
                  lessonspaceSessionId,
                  lessonspaceRecordingLink,
                });
              }
            }
          }
        }
      }

      const engTotal = engHigh + engMed + engLow;
      let avgEng = 'N/A';
      if (engTotal > 0) {
        if (engHigh / engTotal >= 0.5) avgEng = 'High';
        else if ((engHigh + engMed) / engTotal >= 0.5) avgEng = 'Medium';
        else avgEng = 'Low';
      }

      const tutorBreakdown = Object.values(tutorMap).sort((a, b) => b.sessions - a.sessions);

      setDetail({
        name: studentName,
        totalSessions: sessData.length,
        totalHours: Math.round(totalMinutes / 60 * 10) / 10,
        tutorCount: Object.keys(tutorMap).length,
        avgEngagement: avgEng,
        totalIssues,
        tutorBreakdown,
        reviewMoments,
        enthusiasmSignals,
        recentSessions: sessData.map(s => ({
          id: s.id,
          tutor: s.tutor_name,
          subject: s.subject_covered,
          date: s.lesson_timestamp,
          duration: s.duration_minutes,
          engagement: s.student_engagement_level,
          issues: countOpenIssues(risksBySession[s.id], issuePolicy),
        })),
      });
    } catch (err) {
      console.error('Error loading student detail:', err);
    }
    setLoading(false);
  };

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const engColor = (eng) => {
    const e = (eng || '').toLowerCase();
    if (e === 'high') return 'text-emerald-400 bg-emerald-400/10';
    if (e === 'medium') return 'text-amber-400 bg-amber-400/10';
    if (e === 'low') return 'text-red-400 bg-red-400/10';
    return isDark ? 'text-[#52525b] bg-[#18181b]' : 'text-[#999] bg-[#f5f5f5]';
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className={`h-6 w-32 rounded ${isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`} />
        <div className={`h-8 w-48 rounded ${isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`} />
        <div className="grid grid-cols-5 gap-3">
          {[1,2,3,4,5].map(i => <div key={i} className={`${cardClass} h-20`} />)}
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="space-y-6">
        <button onClick={() => navigate('/students')} className={`flex items-center gap-2 text-sm ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}>
          <ArrowLeft className="h-4 w-4" /> Back to students
        </button>
        <p className={`text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Student not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/students')} className={`flex items-center gap-2 text-sm ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}>
        <ArrowLeft className="h-4 w-4" /> Back to students
      </button>

      <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
        {detail.name}
      </h1>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Sessions', value: detail.totalSessions, icon: BookOpen },
          { label: 'Hours', value: `${detail.totalHours}h`, icon: Clock },
          { label: 'Tutors', value: detail.tutorCount, icon: Users },
          { label: 'Engagement', value: detail.avgEngagement, icon: Star, engBadge: true },
          { label: 'Issues', value: detail.totalIssues, icon: AlertTriangle, warn: detail.totalIssues > 0 },
        ].map(kpi => (
          <div key={kpi.label} className={`${cardClass} p-4`}>
            <div className="flex items-center justify-between mb-1">
              <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{kpi.label}</span>
              <kpi.icon className={`h-3.5 w-3.5 ${kpi.warn ? 'text-amber-400' : isDark ? 'text-[#52525b]' : 'text-[#999]'}`} strokeWidth={1.5} />
            </div>
            {kpi.engBadge ? (
              <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${engColor(detail.avgEngagement)} ${detail.avgEngagement === 'N/A' ? 'cursor-help underline decoration-dotted underline-offset-2' : ''}`} title={detail.avgEngagement === 'N/A' ? ENG_NA_TIP : undefined}>{kpi.value}</span>
            ) : (
              <div className={`text-xl font-semibold font-[Manrope] ${kpi.warn ? 'text-amber-400' : isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{kpi.value}</div>
            )}
          </div>
        ))}
      </div>

      {/* Tutor Breakdown */}
      <div className={cardClass}>
        <div className="p-5 pb-3">
          <h3 className={`text-sm font-medium ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Tutor Breakdown</h3>
        </div>
        <div className={`divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
          <div className={`grid grid-cols-6 gap-3 px-5 py-2 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
            <span className="col-span-2">Tutor</span>
            <span>Sessions</span>
            <span>Hours</span>
            <span>Engagement</span>
            <span>Issues</span>
          </div>
          {detail.tutorBreakdown.map(t => {
            const total = t.engHigh + t.engMed + t.engLow;
            return (
              <button
                key={t.name}
                onClick={() => navigate(`/tutors/${encodeURIComponent(t.name)}`)}
                className={`w-full grid grid-cols-6 gap-3 px-5 py-3 items-center text-left transition-colors ${isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#fafafa]'}`}
              >
                <div className="col-span-2 flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg accent-gradient flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-medium">{t.name.charAt(0)}</span>
                  </div>
                  <span className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{t.name}</span>
                </div>
                <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{t.sessions}</span>
                <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{Math.round(t.minutes / 60 * 10) / 10}h</span>
                <div className="flex items-center gap-1">
                  {total > 0 && (
                    <div className="flex h-1.5 w-16 rounded-full overflow-hidden">
                      {t.engHigh > 0 && <div className="bg-emerald-400" style={{ width: `${(t.engHigh / total) * 100}%` }} />}
                      {t.engMed > 0 && <div className="bg-amber-400" style={{ width: `${(t.engMed / total) * 100}%` }} />}
                      {t.engLow > 0 && <div className="bg-red-400" style={{ width: `${(t.engLow / total) * 100}%` }} />}
                    </div>
                  )}
                </div>
                <span className={`text-sm ${t.issues > 0 ? 'text-amber-400' : isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                  {t.issues > 0 && <AlertTriangle className="h-3 w-3 inline mr-1" />}{t.issues}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Review-Worthy Moments + Enthusiasm Signals */}
      {(detail.reviewMoments.length > 0 || detail.enthusiasmSignals.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Review-Worthy Moments */}
          <div className={cardClass}>
            <div className="p-5 pb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Quote className={`h-4 w-4 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`} strokeWidth={1.5} />
                <h3 className={`text-sm font-medium ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Review-Worthy Moments</h3>
                <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{detail.reviewMoments.length}</span>
              </div>
              {detail.reviewMoments.length > 0 && (
                <button
                  onClick={() => setOpenDialog('review')}
                  className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors ${
                    isDark ? 'hover:bg-[#18181b] text-[#a1a1aa] hover:text-white' : 'hover:bg-[#f0f0f0] text-[#666] hover:text-[#1a1a1a]'
                  }`}
                  aria-label="Expand review-worthy moments"
                >
                  <Maximize2 className="h-3 w-3" /> Expand
                </button>
              )}
            </div>
            <div className={`divide-y max-h-[400px] overflow-y-auto ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
              {detail.reviewMoments.length === 0 ? (
                <div className={`px-5 py-8 text-center text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>No review-worthy moments recorded</div>
              ) : detail.reviewMoments.slice(0, 20).map((m, i) => (
                <div
                  key={i}
                  onClick={() => navigate(`/lessons/${m.sessionId}`)}
                  className={`px-5 py-3 cursor-pointer transition-colors ${isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#fafafa]'}`}
                >
                  <div className={`text-xs mb-1 flex items-center gap-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                    <span>{m.date ? new Date(m.date).toLocaleDateString([], { day: 'numeric', month: 'short' }) : '--'} · {m.tutor}</span>
                    {m.timestampDisplay && (
                      <span className={`inline-flex items-center gap-1 ${isDark ? 'text-[#71717a]' : 'text-[#888]'}`}>
                        <Clock className="h-3 w-3" /> {m.timestampDisplay}
                      </span>
                    )}
                  </div>
                  {m.quote && (
                    <p className={`text-sm italic mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#555]'}`}>"{m.quote}"</p>
                  )}
                  {m.reason && (
                    <p className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{m.reason}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Enthusiasm Signals */}
          <div className={cardClass}>
            <div className="p-5 pb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className={`h-4 w-4 text-emerald-400`} strokeWidth={1.5} />
                <h3 className={`text-sm font-medium ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Enthusiasm Signals</h3>
                <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{detail.enthusiasmSignals.length}</span>
              </div>
              {detail.enthusiasmSignals.length > 0 && (
                <button
                  onClick={() => setOpenDialog('enthusiasm')}
                  className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors ${
                    isDark ? 'hover:bg-[#18181b] text-[#a1a1aa] hover:text-white' : 'hover:bg-[#f0f0f0] text-[#666] hover:text-[#1a1a1a]'
                  }`}
                  aria-label="Expand enthusiasm signals"
                >
                  <Maximize2 className="h-3 w-3" /> Expand
                </button>
              )}
            </div>
            <div className={`divide-y max-h-[400px] overflow-y-auto ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
              {detail.enthusiasmSignals.length === 0 ? (
                <div className={`px-5 py-8 text-center text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>No enthusiasm indicators recorded</div>
              ) : detail.enthusiasmSignals.slice(0, 20).map((s, i) => (
                <div
                  key={i}
                  onClick={() => navigate(`/lessons/${s.sessionId}`)}
                  className={`px-5 py-3 cursor-pointer transition-colors ${isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#fafafa]'}`}
                >
                  <div className={`text-xs mb-1 flex items-center gap-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                    <span>{s.date ? new Date(s.date).toLocaleDateString([], { day: 'numeric', month: 'short' }) : '--'} · {s.tutor}</span>
                    {s.timestampDisplay && (
                      <span className={`inline-flex items-center gap-1 ${isDark ? 'text-[#71717a]' : 'text-[#888]'}`}>
                        <Clock className="h-3 w-3" /> {s.timestampDisplay}
                      </span>
                    )}
                  </div>
                  {s.quote ? (
                    <p className={`text-sm italic mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#555]'}`}>"{s.quote}"</p>
                  ) : (
                    <p className={`text-xs italic mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Quote not captured — open lesson to view.</p>
                  )}
                  {s.description && (
                    <p className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{s.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <SignalsDialog
        open={openDialog === 'review'}
        onOpenChange={(v) => setOpenDialog(v ? 'review' : null)}
        title="Review-Worthy Moments"
        icon={Quote}
        accentClass={isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}
        entries={detail.reviewMoments}
        kind="review"
      />
      <SignalsDialog
        open={openDialog === 'enthusiasm'}
        onOpenChange={(v) => setOpenDialog(v ? 'enthusiasm' : null)}
        title="Enthusiasm Signals"
        icon={Sparkles}
        accentClass="text-emerald-400"
        entries={detail.enthusiasmSignals}
        kind="enthusiasm"
      />

      {/* Session History */}
      {(() => {
        const allSessions = detail.recentSessions || [];
        const totalPages = Math.max(1, Math.ceil(allSessions.length / SESSIONS_PER_PAGE));
        const safePage = Math.min(sessionPage, totalPages - 1);
        const pageRows = allSessions.slice(safePage * SESSIONS_PER_PAGE, (safePage + 1) * SESSIONS_PER_PAGE);
        return (
          <div className={cardClass}>
            <div className="p-5 pb-3 flex items-center justify-between">
              <h3 className={`text-sm font-medium ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Session History</h3>
              <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{allSessions.length} total</span>
            </div>
            <div className={`divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
              <div className={`grid grid-cols-6 gap-3 px-5 py-2 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                <span className="col-span-2">Date</span>
                <span>Tutor</span>
                <span>Subject</span>
                <span>Duration</span>
                <span>Engagement</span>
              </div>
              {pageRows.map(s => (
                <div
                  key={s.id}
                  onClick={() => navigate(`/lessons/${s.id}`)}
                  className={`grid grid-cols-6 gap-3 px-5 py-3 items-center cursor-pointer transition-colors ${isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#fafafa]'}`}
                >
                  <span className={`col-span-2 text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                    {s.date ? new Date(s.date).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '--'}
                  </span>
                  <span className={`text-sm truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{s.tutor || '--'}</span>
                  <span className={`text-sm truncate ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{s.subject || '--'}</span>
                  <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{s.duration ? `${s.duration}m` : '--'}</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full inline-block w-fit ${engColor(s.engagement)}`}>
                    {s.engagement || '--'}
                  </span>
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className={`flex items-center justify-center gap-2 py-3 border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                <button
                  onClick={() => setSessionPage(Math.max(0, safePage - 1))}
                  disabled={safePage === 0}
                  className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-[#18181b] disabled:opacity-30' : 'hover:bg-[#f0f0f0] disabled:opacity-30'}`}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                  Page {safePage + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setSessionPage(Math.min(totalPages - 1, safePage + 1))}
                  disabled={safePage >= totalPages - 1}
                  className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-[#18181b] disabled:opacity-30' : 'hover:bg-[#f0f0f0] disabled:opacity-30'}`}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default StudentDetail;
